import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { expenseCatalogsApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface ExpenseCatalogImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExpenseCatalogImportSheet({ open, onOpenChange }: ExpenseCatalogImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import expense items"
      description="Upload an .xlsx or .csv. Rows are matched to existing items by name."
      onImport={async (file, { idempotencyKey }) => {
        const res = await expenseCatalogsApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          unchanged: res.data.unchanged,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            // Pass structured errors straight through — the generic
            // ImportSheet renders `{ code, message, field }` objects with a
            // field chip next to each message.
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.expenseCatalogs.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await expenseCatalogsApi.exportTemplate();
          triggerDownload(blob, filename ?? 'expense-catalogs-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}
